import { useVideoWallAnalytics } from '../hooks/useVideoWallAnalytics'
import { useVideoWallShortcut } from '../hooks/useVideoWallShortcut'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'

import { DarkProvider } from '@hivewatch/dark'

import { SALES_VIDEO_WALL_CAMERAS } from '../constants/salesCameras'
import { VideoWall } from './VideoWall'
import { SalesVideoWall } from './sales/SalesVideoWall'

/**
 * Global entry point for the Video Wall. Bound once in `_app.tsx` behind
 * `FeatureFlag.videoWall`, so it stays mounted for the whole session: the "W"
 * shortcut listener (via {@link useVideoWallShortcut}) and open/close analytics
 * (via {@link useVideoWallAnalytics}) live here and must run regardless of the
 * overlay's visibility.
 *
 * When open, the wall renders inside a single `<DarkProvider>` — the rebrand
 * (Chakra v3) components resolve their theme from it while the Chakra v1
 * `VideoPlayer` in each tile continues to resolve its own v1 provider from the
 * app root.
 */
export const VideoWallOverlay = () => {
  const { isOpen } = useVideoWallShortcut()
  // cannedVideo is on only in the Sales env, where there are no cloud cameras
  // for the normal picker — render the seeded, non-editable fork instead
  // (ENG-32238).
  const isCannedVideoEnabled = useFeatureFlag(FeatureFlag.cannedVideo)
  // The seeded wall bypasses the redux `cells` state the analytics count reads
  // from, so pass its fixed camera count explicitly.
  useVideoWallAnalytics(
    isCannedVideoEnabled ? SALES_VIDEO_WALL_CAMERAS.length : undefined
  )

  if (!isOpen) {
    return null
  }

  return (
    <DarkProvider>
      {isCannedVideoEnabled ? <SalesVideoWall /> : <VideoWall />}
    </DarkProvider>
  )
}
